/**
 * Incremental Server-Sent-Events reader for `data: {json}` streams.
 *
 * Buffers partial lines across network reads — a JSON event split across two
 * TCP segments is reassembled instead of being silently dropped (which
 * previously caused missing words in streamed chat responses).
 */
export type SseEvent = Record<string, unknown>;

export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: SseEvent) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const drain = (flush: boolean) => {
    const lines = buffer.split('\n');
    // The final segment may be an incomplete line — keep it buffered for the
    // next read unless we're flushing at end-of-stream.
    buffer = flush ? '' : lines.pop() ?? '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.slice(5).trim();
      if (!payload) continue;
      let event: SseEvent;
      try {
        event = JSON.parse(payload) as SseEvent;
      } catch {
        // A complete but unparseable line — log and move on. (Partial lines
        // never reach this point; they stay in the buffer.)
        console.warn('Skipping unparseable SSE line');
        continue;
      }
      onEvent(event);
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      drain(false);
    }
    buffer += decoder.decode();
    drain(true);
  } finally {
    reader.releaseLock();
  }
}

/** Read a useful error message from a non-OK API response body. */
export async function readApiErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    const message = (body as { error?: { message?: string } })?.error?.message;
    if (message) return message;
  } catch {
    // Non-JSON body — use the fallback.
  }
  if (res.status === 429) return 'Too many requests — please wait a minute and try again.';
  return fallback;
}
